import React, { useEffect, useRef, useState } from "react";

export function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

export function Reveal({ children, delay = 0, as: Tag = "div", className = "", ...rest }) {
  const [ref, seen] = useInView();
  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`nm-reveal ${seen ? "is-in" : ""} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// Word-by-word masked reveal, like the Catalyst hero title
export function WordTitle({ text, className = "", as: Tag = "h1", stagger = 90, start = 150 }) {
  const [ref, seen] = useInView(0.2);
  return (
    <Tag ref={ref} className={`nm-title ${className}`} aria-label={text}>
      {text.split(" ").map((w, i) => (
        <React.Fragment key={i}>
          <span className="nm-word-mask" aria-hidden="true">
            <span
              className="nm-word"
              style={{ animationDelay: `${start + i * stagger}ms`, animationPlayState: seen ? "running" : "paused" }}
            >
              {w}
            </span>
          </span>{" "}
        </React.Fragment>
      ))}
    </Tag>
  );
}

// Small grey pill label used as section subtitle (Catalyst "subtitle")
export function Pill({ children, className = "", white = false }) {
  return (
    <span
      className={`inline-flex h-7 items-center rounded-full px-4 text-[12px] font-medium tracking-[-0.01em] text-black ${
        white ? "bg-white" : "bg-[#f7f7f7]"
      } ${className}`}
    >
      {children}
    </span>
  );
}

export function CountDown({ from, to = 0, duration = 1600 }) {
  const [ref, seen] = useInView(0.4);
  const [val, setVal] = useState(from);
  useEffect(() => {
    if (!seen) return undefined;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, from, to, duration]);
  return <span ref={ref}>{val}</span>;
}
